import { VOICE_MAXIMUM_CAPTIONS } from '@/constants/assistantVoice';

export type VoiceCaptionRole = 'user' | 'assistant' | 'note';

export interface VoiceCaption {
  id: string;
  role: VoiceCaptionRole;
  text: string;
  isFinal: boolean;
}

function capped(captions: VoiceCaption[]): VoiceCaption[] {
  return captions.length > VOICE_MAXIMUM_CAPTIONS
    ? captions.slice(captions.length - VOICE_MAXIMUM_CAPTIONS)
    : captions;
}

function upsert(
  captions: VoiceCaption[],
  id: string,
  update: (existing: VoiceCaption | undefined) => VoiceCaption
): VoiceCaption[] {
  const index = captions.findIndex(function sameCaption(caption) {
    return caption.id === id;
  });
  if (index < 0) {
    return capped([...captions, update(undefined)]);
  }
  const next = [...captions];
  next[index] = update(captions[index]);
  return next;
}

export function withAssistantDelta(
  captions: VoiceCaption[],
  itemId: string,
  delta: string
): VoiceCaption[] {
  return upsert(captions, itemId, function appendDelta(existing) {
    return {
      id: itemId,
      role: 'assistant',
      text: existing === undefined ? delta : existing.text + delta,
      isFinal: false,
    };
  });
}

export function withFinalAssistantText(
  captions: VoiceCaption[],
  itemId: string,
  text: string
): VoiceCaption[] {
  return upsert(captions, itemId, function finalize() {
    return { id: itemId, role: 'assistant', text, isFinal: true };
  });
}

export function withUserPlaceholder(captions: VoiceCaption[], itemId: string): VoiceCaption[] {
  return upsert(captions, itemId, function placeholder(existing) {
    return existing === undefined
      ? { id: itemId, role: 'user', text: '', isFinal: false }
      : existing;
  });
}

export function withUserTranscript(
  captions: VoiceCaption[],
  itemId: string,
  transcript: string
): VoiceCaption[] {
  return upsert(captions, itemId, function finalizeUser() {
    return { id: itemId, role: 'user', text: transcript.trim(), isFinal: true };
  });
}

export function withNote(captions: VoiceCaption[], id: string, text: string): VoiceCaption[] {
  return capped([...captions, { id, role: 'note', text, isFinal: true }]);
}

export function visibleCaptions(captions: VoiceCaption[]): VoiceCaption[] {
  return captions.filter(function hasText(caption) {
    return caption.text.length > 0 || caption.isFinal === false;
  });
}
